import React, { Fragment, useState, useContext, useEffect } from 'react';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';

import { Grid, Button, Paper, Grow, InputLabel } from '@material-ui/core';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import NoteAddOutlinedIcon from '@material-ui/icons/NoteAddOutlined';
import CustomInput from '../../../components/CustomInput/CustomInput';
import CustomSelect from '../../../components/CustomSelect/CustomSelect';
import CustomRadioButton from '../../../components/CustomRadioButton/CustomRadioButton';
import { DateBox } from 'devextreme-react/date-box';
/** mobx state store */
import BookingStoreContext from '../../../stores/bookingStore';

/** Custom hooks */
import useDropdown from '../../../hooks/useDropdown';

// custom component
import { getApi } from '../../../api';
import Spinner from '../../../components/spinner/Spinner';
import PopupBooking from './PopupBooking';
import BookingDocument from './bookingDocument';
import { travel_type, amountPassenger } from '../../../utils/constants';
import { updateBooking, updateBookingFiles, deleteBookingFile, downloadBookingFile } from './service';

const useStyles = makeStyles(theme => ({
  root: {
    height: 180,
  },
  container: {
    display: 'flex',
  },
  paper: {
    border: 'none',
    boxShadow: 'none'
  },
  svg: {
    width: 100,
    height: 100,
  },
  polygon: {
    fill: theme.palette.common.white,
    stroke: theme.palette.divider,
    strokeWidth: 1,
  },
  bookingContainer: {
    backgroundColor: theme.palette.primary.main,
    '& label,p': {
      color: theme.palette.primary.contrastText,
      margin: '4px 0 0 10px'
    },
    '& .MuiInputBase-root': {
      margin: '4px 0 0 10px'
    },
    '& .MuiSelect-select:focus': {
      backgroundColor: theme.palette.primary.contrastText,
    },
    '& .dx-texteditor.dx-editor-outlined': {
      borderRadius: 0,
      margin: '4px 10px 0 10px'
    },
    '& .dx-texteditor-input': {
      height: 37,
      fontSize: '1em',
      paddingLeft: 5
    },
    '& .MuiInputBase-input': {
      borderRadius: 0,
    },
    '& .MuiInputBase-fullWidth': {
      width: 'auto'
    },
    '& .documents': {
      '& .document-row': {
        backgroundColor: theme.palette.primary.contrastText,
        border: `solid 1px ${theme.palette.helpers.gray}`,
        borderBottom: 'none',
        margin: '0 10px',
        '& div': {
          display: 'flex',
          alignItems: 'center',
        },
        '& label': {
          paddingLeft: 5,
          color: theme.palette.primary.main
        },
        '& .header': {
          padding: 5,
          color: theme.palette.secondary.main
        }
      }
    }
  },
  button: {
    margin: '2px 0 0 10px'
  }
}));

const Booking = ({ booking, removeBooking, onClose, onBookingEvent }) => {
  const classes = useStyles();
  const bookingStore = useContext(BookingStoreContext);
  const loggedUser = JSON.parse(localStorage.currentUser);
  const [toggleConfirmBooking, setToggleConfirmBooking] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState({ driverMessage: '', vehicleMessage: '' });
  const { drivers, vehicles } = bookingStore;
  const { enqueueSnackbar } = useSnackbar();
  const [pendingBooking, setPendingBooking] = useState({
    ...booking,
    service: {
      driver_id: null,
      vehicle_id: null,
    },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [checked, setChecked] = useState(true);
  const [validationMessage, setValidationMessage] = useState(false);

  const [toggleAcceptBooking, setToggleAcceptBooking] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editBooking, setEditBooking] = useState({});
  const [validateBooking, setValidateBooking] = useState({
    haveOrigin: true,
    haveDestination: true,
    haveSecondOrigin: true,
    haveSecondDestination: true
  });
  const minDate = new Date(moment().subtract(1, 'seconds'));
  const maxDate = new Date(moment().add(12, 'months'));

  const [vehicleId, VehicleDropdown] = useDropdown(
    'Vehículo',
    booking.service ? booking.service.vehicle_id : '',
    vehicles.map(item => item),
  );
  const [driverId, DriverDropdown] = useDropdown(
    'Conductor',
    booking.service ? booking.service.driver_id : '',
    drivers.map(item => item),
  );

  useEffect(() => {
    if (toggleAcceptBooking) {
      handleClosed();
      bookingStore.setValue('isLoading', true);
    } else {
      setPendingBooking({
        ...booking,
        service: {
          vehicle_id: vehicleId,
          driver_id: driverId,
        },
      });
    }
  }, [vehicleId, driverId, setPendingBooking, booking, toggleAcceptBooking, bookingStore]);

  const handleClosed = () => {
    onClose();
    setChecked(prev => !prev);
  };

  const handleRejectBooking = async () => {
    setIsLoading(true);
    const response = await getApi().put(`/users/${booking.user.id}/bookings/${booking.id}`, {status: 'denied'});
    setIsLoading(false);

    if (response.status === 200) {
      handleClosed();
      bookingStore.setValue('isLoading', true);
      if (onBookingEvent) {
        onBookingEvent({ id: booking.id });
      }
    }
  };

  const handleApprovedBooking = async () => {
    if (pendingBooking.service.driver_id && pendingBooking.service.vehicle_id) {
      setValidationMessage(false);
      setIsLoading(true);

      let responseBooking = null;
      let responseService = null;

      // first create service
      responseService = await getApi().post(`/services`, {
        service: {
          driver_id: pendingBooking.service.driver_id,
          vehicle_id: pendingBooking.service.vehicle_id,
          booking_id: pendingBooking.id,
          status: 'scheduled',
        },
      });

      // second create booking
      if (responseService && responseService.status === 200) {
        const booking = {
          ...pendingBooking,
          status: 'approved',
          service_id: responseService.data.id,
          restrictions: {
            allow_conflicts: false,
          },
        };

        try {
          responseBooking = await getApi().put(
            `/users/${pendingBooking.user.id}/bookings/${pendingBooking.id}`,
            booking,
          );
        } catch (error) {
          if (error.status === 400) {
            // show confirmation message
            console.log(error);
            if (error.data.errors) {
              const driver = error.data.errors.driver ? error.data.errors.driver[0] : '';
              const vehicle = error.data.errors.vehicle ? error.data.errors.vehicle[0] : '';
              setConfirmMessage({
                driverMessage: driver ? `Chofer: ${driver}` : '',
                vehicleMessage: vehicle ? `Vehículo: ${vehicle}` : '',
                booking: booking,
              });
            }
            setToggleConfirmBooking(true);
          }
        }
      }

      setIsLoading(false);

      if (responseBooking && responseBooking.status === 200) {
        handleClosed();
        bookingStore.setValue('isLoading', true);
        if (onBookingEvent) {
          onBookingEvent({ id: booking.id })
        }
      }
    } else {
      setValidationMessage(true);
    }
  };

  const canAssign = () => {
    const isNotCollaboratorOrDriver = !['collaborator', 'driver'].find(type => type === loggedUser.role);
    return isOnTime(booking) && isNotCollaboratorOrDriver;
  }

  const canEdit = () => {
    const isNotDriver = 'driver' !== loggedUser.role;
    const isNotDone = !!('requested' === booking.status);
    return isOnTime(booking) && isNotDriver && isNotDone;
  }

  const canChangeBookingType = () => {
    return 'collaborator' !== loggedUser.role;
  }

  const isOnTime = (selectedBooking) => {
    const departureDate = new Date(selectedBooking.departure_datetime);
    const now = new Date();
    now.setMinutes(now.getMinutes() + 5);
    return departureDate >= now;
  }

  const showStartDestinyLabel = () => {
    return !['roundtrip', 'one_way'].find(type => type === editBooking.travel_type);
  }

  const showReturnLabel = () => {
    return 'one_way' !== editBooking.travel_type;
  }

  const startEdit = () => {
    setEditBooking(Object.assign({}, booking));
    setEditMode(true);
  }

  const cancelEdit = () => {
    setEditMode(false);
    setEditBooking({});
  }

  const handleSelectsChange = name => event => {
    const newValue = name === 'travel_type' ? event : event.target.value
    setEditBooking({
      ...editBooking,
      [name]: newValue,
    });
  };

  const checkBooking = () => {
    let [result, haveOrigin, haveDestination, haveSecondOrigin, haveSecondDestination ] = [ true, true, true, true, true ];

    if (editBooking.origin === '') {
      haveOrigin = false;
      result = false;
    }

    if (editBooking.travel_type === 'multi_places' && editBooking.second_origin === '') {
      haveSecondOrigin = false;
      result = false;
    }

    if (editBooking.destination === '') {
      haveDestination = false;
      result = false;
    }

    if (editBooking.travel_type === 'multi_places' && editBooking.second_destination === '') {
      haveSecondDestination = false;
      result = false;
    }

    if (!isOnTime(editBooking)) {
      enqueueSnackbar('La fecha y hora de salida programada debe ser mayor a la actual', {
        variant: 'error',
      });
      result = false;
    }

    if (
      editBooking.travel_type !== 'one_way' &&
      new Date(editBooking.departure_datetime).getTime() >= new Date(editBooking.return_datetime).getTime()
    ) {
      enqueueSnackbar('La fecha de regreso debe ser mayor a la fecha de salida', {
        variant: 'error',
      });
      result = false;
    }
    setValidateBooking({
      ...validateBooking,
      haveOrigin,
      haveDestination,
      haveSecondOrigin,
      haveSecondDestination
    });
    return result;
  };

  const saveBooking = async () => {
    if (checkBooking()) {
      try {
        setIsLoading(true);
        const response = await updateBooking({...editBooking, user_id: editBooking.user.id });
        if (response && response.status === 200) {
          bookingStore.setValue('isLoading', true);
          handleClosed();
          setIsLoading(false);
          if (onBookingEvent) {
            onBookingEvent({ id: editBooking.id });
          }
        }
        cancelEdit()
      } catch (error) {
        if (error.status === 400) {
          enqueueSnackbar('Algo ha pasado, por favor revisar que los datos suministrados esten correctos', {
            variant: 'error',
          });
        }
      }
    }
  }

  const addDocuments = async (documents) => {
    setIsLoading(true);
    const response = await updateBookingFiles( { documents, id: editBooking.id, user_id: editBooking.user.id } );
    if (response && response.status === 200) {
      bookingStore.setValue('isLoading', true);
      setIsLoading(false);
    }
  }

  const isInDocuments = file => {
    return !!booking.documents && !!booking.documents.find( d => 
      file.name === d.filename && file.size === d.byte_size && file.type === d.content_type
    );
  }

  const handleNewFiles = event => {
    const files = event.target.files;
    const docs = [];
    for (let i = 0; i < files.length; i++) {
      if (!isInDocuments(files[i])) {
        docs.push(files[i])
      }
    }
    if (!!docs.length) {
      addDocuments(docs);
    }
  }

  const deleteDocument = async (id) => {
    setIsLoading(true);
    const response = await deleteBookingFile({ id: booking.id, documentId: id });
    if (response && response.status === 204) {
      bookingStore.setValue('isLoading', true);
      setIsLoading(false);
    }
  }

  const downloadDocument = document => {
    downloadBookingFile({ id: booking.id, document });
  }

  const getObervations = () => {
    let observations = booking.observations ? booking.observations : '';
    if ((loggedUser.role === 'admin' || loggedUser.role === 'programmer' ) && !!booking.rating_value) {
      const rateText = `Calificación: ${booking.rating_value}. Comentario: ${booking.rating_comment}.`;
      observations = !observations.length ? rateText : `${observations}. ${rateText}`;
    }
    return !observations.length ? '--' : observations;
  }

  const renderDocuments = () => {
    return (
      <Grid className="documents" container item sm={12} xs={12}>
        <InputLabel shrink >
          Documentos
        </InputLabel>
        <Grid className="document-row" container item sm={12} xs={12}>
          <Grid item sm={4} xs={4}>
            <InputLabel className="header" shrink >
              Nombre
            </InputLabel>
          </Grid>
          <Grid item sm={3} xs={3}>
            <InputLabel className="header" shrink >
              Tamaño
            </InputLabel>
          </Grid>
          <Grid item sm={4} xs={4}>
            <InputLabel className="header" shrink >
              Tipo
            </InputLabel>
          </Grid>
          <Grid item sm={1} xs={1}>
          </Grid>
        </Grid>
        { booking.documents.map( (doc, index) => {
            return (
              <BookingDocument key={`&{doc.id}-${index}`} showDelete={canEdit() && editMode}
                document={doc} onDelete={deleteDocument} onDownload={downloadDocument} />
            );
          })
        }
      </Grid>
    );
  }

  const renderMessages = () => {
    const { user, service } = booking;
    const { driver } = service;
    return (
      <Grid className="documents" container item sm={12} xs={12}>
        <InputLabel shrink >
          Mensajería
        </InputLabel>
        <Grid className="document-row" container item sm={12} xs={12}>
          <Grid item sm={3} xs={4}>
            <InputLabel className="header" shrink >
              Remitente
            </InputLabel>
          </Grid>
          <Grid item sm={7} xs={8}>
            <InputLabel className="header" shrink >
              Mensaje
            </InputLabel>
          </Grid>
          <Grid item sm={2} xs={false}>
            <InputLabel className="header" shrink >
              Fecha
            </InputLabel>
          </Grid>
        </Grid>
        { booking.messages.map( (message, index) => {
            return (
              <Grid key={message.id} className="document-row" container item sm={12} xs={12}>
                <Grid item sm={3} xs={4}>
                  <InputLabel shrink >
                    { user.id === message.sender_id ? user.full_name : driver.full_name}
                  </InputLabel>
                </Grid>
                <Grid item sm={7} xs={8}>
                  <InputLabel shrink >
                    { message.body }
                  </InputLabel>
                </Grid>
                <Grid item sm={2} xs={false}>
                  <InputLabel shrink >
                    { message.created_at
                          ? new Date(message.created_at).toLocaleString('es-CR', { hour12: true })
                          : '--' }
                  </InputLabel>
                </Grid>
              </Grid>
            );
          })
        }
      </Grid>
    );
  }

  return (
    <Grow in={checked}>
      <Paper className={classes.paper}>
        <div className={classes.bookingContainer} >
          <Grid container spacing={1}>
            <Grid item xs={6} sm={3}>
              <InputLabel shrink >
                Solicitante:
              </InputLabel>
              <p>{booking.user.full_name}</p>
            </Grid>
            <Grid item xs={6} sm={3}>
              <InputLabel shrink >
                Origen:
              </InputLabel>
                { editMode && canChangeBookingType() ? (
                  <Fragment>
                    <CustomInput
                      id="inputOrigin"
                      labelText="Origen"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        value: editBooking.origin,
                        onChange: handleSelectsChange('origin'),
                      }}
                    />
                    {validateBooking.haveOrigin === false && (
                      <span
                        style={{
                          display: 'block',
                          color: 'red',
                          marginTop: 5,
                          marginLeft: 10
                        }}
                      >
                        Debe insertar un Origen
                      </span>
                    )}
                  </Fragment>
                ) : 
                ( <p>{booking.origin}</p> )
              }
            </Grid>
            <Grid item xs={6} sm={3}>
              <InputLabel shrink >
                Destino:
              </InputLabel>
                { editMode && canChangeBookingType() ? (
                  <Fragment>
                    <CustomInput
                      id="txtDestination"
                      labelText="Destino"
                      formControlProps={{
                        fullWidth: true,
                      }}
                      inputProps={{
                        value: editBooking.destination,
                        onChange: handleSelectsChange('destination'),
                      }}
                    />
                    {validateBooking.haveDestination === false ? (
                      <span
                        style={{
                          display: 'block',
                          color: 'red',
                          marginTop: 5,
                          marginLeft: 10
                        }}
                      >
                        Debe insertar un destino
                      </span>
                    ) : (
                      ''
                    )}
                  </Fragment>
                ) : 
                ( <p>{booking.destination}</p>)
              }
            </Grid>
            <Grid item xs={6} sm={3}>
              <InputLabel shrink >
                Fecha de salida:
              </InputLabel>
              { editMode ? (
                <DateBox
                  type="datetime"
                  value={editBooking.departure_datetime}
                  width={'auto'}
                  max={maxDate}
                  min={minDate}
                  onValueChanged={({ value }) =>
                    setEditBooking({
                      ...editBooking,
                      departure_datetime: value,
                    })
                  }
                /> 
                ) : (
                  <p> {new Date(booking.departure_datetime).toLocaleString('es-CR', { hour12: true })} </p>
                )
              }
            </Grid>

            <Grid item xs={6} sm={3}>
              <InputLabel shrink >
                Tipo de reserva:
              </InputLabel>
              { editMode && canChangeBookingType() ? (
                <>
                  <Grid item sm={12} xs={12}>
                    <CustomRadioButton
                      name="radio-button-one-way"
                      label="Solo ida"
                      value="one_way"
                      selectedValue={editBooking.travel_type}
                      handleChange={handleSelectsChange('travel_type')}
                    />
                    <CustomRadioButton
                      name="radio-button-round-trip"
                      label="Ida y vuelta"
                      value="roundtrip"
                      selectedValue={editBooking.travel_type}
                      handleChange={handleSelectsChange('travel_type')}
                    />
                    <CustomRadioButton
                      name="radio-button-multi-places"
                      label="Multidestino"
                      value="multi_places"
                      selectedValue={editBooking.travel_type}
                      handleChange={handleSelectsChange('travel_type')}
                    />
                  </Grid>
                </>
              ) : (
                <p>{travel_type[booking.travel_type]}</p>
              )}
            </Grid>
            <Grid item xs={6} sm={3}>
              {showStartDestinyLabel() && 
                <Fragment>
                  <InputLabel shrink >
                    Origen:
                  </InputLabel>
                  { editMode && canChangeBookingType() ? (
                    <>
                      <CustomInput
                          id="inputSecondOrigin"
                          labelText="Origen"
                          formControlProps={{
                            fullWidth: true,
                          }}
                          inputProps={{
                            value: editBooking.second_origin,
                            onChange: handleSelectsChange('second_origin'),
                          }}
                        />
                      {validateBooking.haveSecondOrigin === false && (
                        <span
                          style={{
                            display: 'block',
                            color: 'red',
                            marginTop: 5,
                            marginLeft: 10
                          }}
                        >
                          Debe insertar un Origen
                        </span>
                      )}
                    </>
                    ) : ( <p>{booking.second_origin}</p> )
                  }
                </Fragment>
              }
            </Grid>
            <Grid item xs={6} sm={3}>
              {showStartDestinyLabel() && 
                <Fragment>
                  <InputLabel shrink >
                    Destino:
                  </InputLabel>
                  { editMode && canChangeBookingType() ? (
                    <>
                      <CustomInput
                        id="inputSecondDestination"
                        labelText="Destino"
                        formControlProps={{
                          fullWidth: true,
                        }}
                        inputProps={{
                          value: editBooking.second_destination,
                          onChange: handleSelectsChange('second_destination'),
                        }}
                      />
                      { validateBooking.haveSecondDestination === false && (
                        <span
                          style={{
                            display: 'block',
                            color: 'red',
                            marginTop: 5,
                            marginLeft: 10
                          }}
                        >
                          Debe insertar un Destino
                        </span>
                      )}
                    </>
                    ) : ( <p>{booking.second_destination}</p> )
                  }
                </Fragment>
              }
            </Grid>
            <Grid item xs={6} sm={3}>
            {showReturnLabel() && 
                <Fragment>
                  <InputLabel shrink >
                    Fecha de regreso:
                  </InputLabel>
                  { editMode ? (
                      <DateBox
                        type="datetime"
                        max={maxDate}
                        min={minDate}
                        width={'auto'}
                        value={editBooking.return_datetime}
                        onValueChanged={({ value }) =>
                          setEditBooking({
                            ...editBooking,
                            return_datetime: value,
                          })
                        }
                      />
                    ) : (
                      <p>
                        {booking.return_datetime
                          ? new Date(booking.return_datetime).toLocaleString('es-CR', { hour12: true })
                          : '--'}
                      </p>
                    )
                  }
                </Fragment>
              }
            </Grid>

            <Grid item xs={12} sm={6}>
              <InputLabel shrink >
                Observaciones:
              </InputLabel>
              { editMode ? (
                  <CustomInput
                    id="observationTxt"
                    labelText="Observaciones"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    inputProps={{
                      value: editBooking.observations,
                      onChange: handleSelectsChange('observations'),
                    }}
                  />
                ) : ( <p>{getObervations()}</p> )
              }
            </Grid>
            <Grid item xs={12} sm={3}>
              <InputLabel shrink >
                Cantidad de pasajeros:
              </InputLabel>
              { editMode ? (
                  <CustomSelect
                    id="selectPassanger"
                    labelText="Cantidad de pasajeros"
                    formControlProps={{
                      fullWidth: true,
                    }}
                    value={editBooking.passengers_number}
                    options={amountPassenger}
                    onChange={handleSelectsChange('passengers_number')}
                  />
                ) : ( <p>{booking.passengers_number}</p> )
              }
            </Grid>
            <Grid item xs={12} sm={3}>
              {' '}
            </Grid>
            {canAssign() && !['completed', 'cancelled'].find(s => s === booking.status) ? (
              <>
                {!editMode && (
                  <>
                    <Grid item xs={6} sm={3}>
                      <DriverDropdown />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <VehicleDropdown />
                    </Grid>
                  </>
                )}
              </>
            ) : (
              <>
                <Grid item xs={6} sm={3}>
                  <InputLabel shrink>Chofer</InputLabel>
                  <p>{booking.service ? booking.service.driver.full_name : '--'}</p>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <InputLabel shrink>Vehículo</InputLabel>
                  <p>{booking.service ? 'Placa-' + booking.service.vehicle.license_plate : '--'}</p>
                </Grid>
              </>
            )}
            {canEdit()  && pendingBooking.status === 'requested' && (
              <Grid item xs={12} sm={6} style={{ paddingTop: '25px' }}>
                {!editMode ? (
                  <>
                    <Button
                      variant="contained"
                      className={classes.button}
                      color="secondary"
                      onClick={() => {
                        startEdit();
                      }}
                      startIcon={<EditOutlinedIcon />}
                    >
                      Editar
                    </Button>
                    { canAssign() && (
                      <>
                        <Button
                          variant="contained"
                          className={classes.button}
                          color="secondary"
                          onClick={() => {
                            handleRejectBooking();
                          }}
                          startIcon={<BlockIcon />}
                        >
                          Rechazar
                        </Button>
                        <Button
                          variant="contained"
                          className={classes.button}
                          color="secondary"
                          onClick={() => {
                            handleApprovedBooking();
                          }}
                          startIcon={<CheckCircleOutlineIcon />}
                        >
                          Asignar
                        </Button>
                      </>
                    )}
                  </>
                ) : (
                    <Fragment>
                      <Button
                        className="action-button"
                        variant="contained"
                        color="secondary"
                        startIcon={<NoteAddOutlinedIcon />}
                        component="label"
                      >
                        Adjuntar documentos
                        <input type="file" style={{ display: 'none' }} multiple onChange={event => handleNewFiles(event)} />
                      </Button>
                      <Button
                        variant="contained"
                        className={classes.button}
                        color="secondary"
                        onClick={() => {
                          cancelEdit();
                        }}
                        startIcon={<BlockIcon />}
                      >
                        Cancelar
                      </Button>
                      <Button
                        variant="contained"
                        className={classes.button}
                        color="secondary"
                        onClick={() => {
                          saveBooking();
                        }}
                        startIcon={<CheckCircleOutlineIcon />}
                      >
                        Modificar
                      </Button>
                    </Fragment>
                  )
                }
              </Grid>
            )}
            {!!booking.documents && !!booking.documents.length && renderDocuments()}
            {!!booking.messages && !!booking.messages.length && renderMessages()}
            <Grid item xs={12} sm={12}>
              {validationMessage && (
                <span
                  style={{
                    display: 'block',
                    color: 'red',
                    marginTop: '5px',
                  }}
                >
                  Debe seleccionar un chofer y un vehículo
                </span>
              )}
            </Grid>
          </Grid>

          <Spinner isLoading={isLoading} />
          {toggleConfirmBooking ? (
            <PopupBooking
              setToggleAcceptBooking={setToggleAcceptBooking}
              setToggleConfirmBooking={setToggleConfirmBooking}
              message={confirmMessage}
            />
          ) : null}
        </div>
      </Paper>
    </Grow>
  );
};

export default Booking;
